import { Collapsible } from '@base-ui/react/collapsible';
import { Menu } from '@base-ui/react/menu';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { useAppSelector } from '@/app/hooks';
import {
  selectIsSidebarCollapsed,
  toggleMobileSidebar,
} from '@/features/sidebar/model/sidebarSlice';
import { Icon, type IconName } from '@/shared/IconCollecion';
import { brand } from '@/brand';

// Адаптация mcms-front MenuItem. Добавлены поля `badge` (число для индикатора
// типа me.news / me.new_tickets) и `visible` (для conditional рендера, типа
// Рефералы который виден только если me.referral_system === true).

export type SidebarMenuItem = {
  iconName: IconName;
  label: string;
  // isNew — пометка «новое»: рисуется пилюлей рядом с подписью (см. NewTag),
  // текст в неё не приходит из словаря и не переводится. Есть и у пункта
  // верхнего уровня, и у подпункта.
  isNew?: boolean;
  sublinks: Array<{ label: string; link: string; isNew?: boolean }>;
  link: string;
  badge?: number | null;
  visible?: boolean;
  // Пути, которые тоже принадлежат этому разделу, хотя пункта меню у них нет:
  // экраны, живущие табами внутри раздела (brand.tabGroups). Без них подсветка
  // гаснет на всех табах, кроме первого, — раздел выглядит покинутым.
  activePaths?: string[];
};

// Вид пунктов задаёт бренд (brand.layout.sidebarMenu): 'icons' — иконка с
// подписью, активный выделен цветом текста; 'pills' — на десктопе только
// подписи, активный залит акцентом (макет telecom). В мобильной шторке иконки
// есть в обоих видах: там меню светлое и на всю ширину, опора на иконку нужна.
//
// Поле читается в рендере, а не константой модуля: так развилку видит тест с
// подменённым брендом (замок «поле layout → вид», см. MenuList.pills.test).
const readIsPills = () => brand.layout.sidebarMenu === 'pills';

// Пилюли. Группа («Потоки») сама не заливается — заливается её активный подпункт,
// а открытая группа просто светлеет, как в макете.
const getPillClassName = (isActive: boolean, hasSublinks: boolean) =>
  cn(
    'group flex w-full items-center justify-start gap-3 rounded-control px-3.5 py-[11px] text-sm font-semibold leading-5 transition-colors',
    isActive && !hasSublinks
      ? 'bg-action-primary text-content-inverse'
      : isActive
        ? 'text-content-primary lg:text-content-inverse'
        : 'text-content-primary hover:bg-surface-hover lg:text-content-inverse-muted lg:hover:bg-surface-sidebar-hover lg:hover:text-content-inverse',
  );

const getSubPillClassName = (isActive: boolean) =>
  cn(
    'w-full rounded-control px-3 py-[9px] text-start text-[13px] font-semibold leading-5 transition-colors',
    isActive
      ? 'bg-action-primary text-content-inverse'
      : 'text-content-primary hover:bg-surface-hover lg:text-content-inverse-muted lg:hover:bg-surface-sidebar-hover lg:hover:text-content-inverse',
  );

const getRootLinkClassName = (isActive: boolean, hasSublinks: boolean, isPills: boolean) =>
  isPills
    ? getPillClassName(isActive, hasSublinks)
    : cn(
    // text-sm font-medium leading-5 = 14/500/20 по Figma-спецификации навбара.
    // font-medium теперь у всех пунктов, не только с sublinks (как было раньше).
    // overflow-hidden: подписи теперь всегда в DOM и гаснут через opacity
    // (не размонтируются), поэтому в свёрнутой 64px-полосе текст клипуем,
    // чтобы не распирал строку во время анимации.
    'group flex max-h-8 w-full items-center justify-start gap-2 overflow-hidden py-1 pl-2 text-sm font-medium leading-5',
    isActive
      ? 'text-content-accent lg:text-content-inverse-accent'
      : 'text-content-primary hover:text-content-accent lg:text-content-inverse-muted lg:hover:text-content-inverse-accent',
  );

// Пометка нового раздела: пилюля с собственной заливкой (цвет не наследуется от
// строки — иначе на тёмном сайдбаре метка теряется среди подписей).
//
// Два тона, потому что фон под пилюлей разный:
// - 'row' — строка аккордеона. На мобиле шторка светлая, поэтому акцентная заливка
//   бренда (serious — синяя, WapClick — зелёная) с белым текстом; на десктопе
//   (lg) сайдбар тёмно-синий, и там пилюля наоборот светлая с тёмным текстом —
//   синее на тёмно-синем читается заметно хуже (сравнивали на рендерах).
// - 'popup' — список подпунктов свёрнутого сайдбара. Он тоже живёт на десктопе, но
//   на белой карточке, поэтому остаётся акцентным.
//
// Текст литералом, а не ключом словаря: это метка состояния, одинаковая на всех
// языках (решение 14.09.2026). В разметке строчные, заглавные даёт uppercase.
const NewTag = ({ context }: { context: 'row' | 'popup' }) => (
  <span
    className={cn(
      'ml-2 shrink-0 rounded-full px-1.5 py-px text-xxs font-bold uppercase leading-4 tracking-wide',
      'bg-action-secondary-accent text-content-inverse',
      context === 'row' && 'lg:bg-content-inverse-accent lg:text-surface-sidebar',
    )}
  >
    new
  </span>
);

// onAccent — бейдж на залитом акцентом пункте (пилюли): красное на красном
// пропало бы, поэтому цвета меняются местами.
const Badge = ({ count, onAccent = false }: { count: number; onAccent?: boolean }) => (
  <span
    className={cn(
      'ml-auto rounded-full px-2 text-xs font-bold leading-5',
      onAccent ? 'bg-content-inverse text-content-accent' : 'bg-action-secondary-accent text-content-inverse',
    )}
  >
    {count}
  </span>
);

// Иконка пункта. В пилюлях на десктопе её нет — только подпись.
const ItemIcon = ({ name, isPills }: { name: IconName; isPills: boolean }) => (
  <span className={cn('shrink-0', isPills ? 'lg:hidden' : 'grow')}>
    <Icon name={name} color="currentColor" />
  </span>
);

const MenuItemWithSublinks = ({
  item,
  index,
  disableCollapse = false,
}: {
  item: SidebarMenuItem;
  index: number;
  disableCollapse?: boolean;
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  // disableCollapse (мобильная шторка): redux-стейт collapse игнорируем —
  // он про десктопный Sidebar (см. комментарий в MenuList).
  const isCollapsed = useAppSelector(selectIsSidebarCollapsed) && !disableCollapse;
  const { iconName, label, sublinks } = item;
  const isActive = Boolean(sublinks?.some((sub) => location.pathname.startsWith(sub.link)));
  const [active, setActive] = useState(isActive);

  useEffect(() => {
    setActive(isActive);
  }, [isActive]);

  const onSubLinkClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    dispatch(toggleMobileSidebar());
  };

  const isPills = readIsPills();
  const rootLinkClassName = getRootLinkClassName(isActive, true, isPills);

  return (
    <li className="relative flex w-full flex-col justify-between" key={index}>
      <Collapsible.Root
        className="flex w-full flex-col"
        open={isCollapsed ? false : active}
        onOpenChange={(open) => {
          // В свёрнутом навбаре панель не раскрываем — sublinks-текст (pl-10)
          // не влезает в узкую полосу. В collapsed подпункты доступны через
          // popup (Menu ниже), а сам Collapsible.Trigger гасим pointer-events,
          // чтобы клик уходил в Menu.Trigger-оверлей.
          if (!isCollapsed) setActive(open);
        }}
      >
        <Collapsible.Trigger
          className={cn(rootLinkClassName, isCollapsed && 'pointer-events-none')}
          title={isCollapsed ? label : undefined}
          // В свёрнутом навбаре триггер выключен: pointer-events сняты, а
          // onOpenChange заглушён. Фокус он при этом принимал, и Enter на нём
          // не делал ничего — пункт «зависал» на первом Tab-стопе, хотя popup
          // открывает лежащий поверх Menu.Trigger. Убираем и из tab-порядка, и
          // из дерева доступности: подпись и роль есть у Menu.Trigger.
          tabIndex={isCollapsed ? -1 : undefined}
          aria-hidden={isCollapsed || undefined}
        >
          <ItemIcon name={iconName} isPills={isPills} />
          {/* Свёрнутый навбар: подпись и стрелка остаются в DOM, но плавно
              гаснут через opacity (строка клипуется overflow-hidden). */}
          <div
            className={cn(
              'flex w-full justify-between whitespace-nowrap transition-opacity duration-200',
              isCollapsed ? 'opacity-0' : 'opacity-100',
            )}
          >
            <span>{label}</span>
          </div>
          <div
            className={cn(
              'flex grow cursor-pointer items-center justify-end transition-opacity duration-200',
              isCollapsed ? 'opacity-0' : 'opacity-100',
            )}
          >
            <Icon
              name="Arrow_down"
              color="currentColor"
              className={cn(isPills ? 'size-4' : 'size-6', 'shrink-0 group-data-[panel-open]:rotate-180')}
            />
          </div>
        </Collapsible.Trigger>

        <Collapsible.Panel className="flex h-[var(--collapsible-panel-height)] flex-col justify-end overflow-hidden text-sm transition-all duration-150 ease-out data-[ending-style]:h-0 data-[starting-style]:h-0 [&[hidden]:not([hidden='until-found'])]:hidden">
          <div
            className={cn(
              'flex flex-col items-start',
              // Пилюли: подпункты под чертой слева, со сдвигом под подпись группы.
              isPills
                ? 'mb-1 ml-3.5 mt-0.5 gap-0.5 border-l-[1.5px] border-line-default pl-2.5 lg:border-line-inverse'
                : 'gap-1',
            )}
          >
            {sublinks.map((subItem, subItemIndex) => (
              <Link
                key={subItemIndex}
                onClick={onSubLinkClick}
                to={subItem.link}
                className={
                  isPills
                    ? getSubPillClassName(subItem.link === location.pathname)
                    : cn(
                        // text-sm font-medium leading-5 = 14/500/20, как и у
                        // top-level пунктов навбара (см. getRootLinkClassName).
                        'w-full py-1.5 pl-10 text-start text-sm font-medium leading-5 first:mt-1',
                        'hover:text-content-accent lg:hover:text-content-inverse-accent',
                        subItem.link === location.pathname &&
                          'rounded-md bg-surface-hover text-content-accent lg:rounded-none lg:bg-transparent lg:text-content-inverse-accent',
                      )
                }
              >
                {subItem.label}
                {subItem.isNew && <NewTag context="row" />}
              </Link>
            ))}
          </div>
        </Collapsible.Panel>
      </Collapsible.Root>

      {/* Свёрнутый навбар: клик по пункту открывает popup с подпунктами
          (механика из mcms-front). Прозрачный Menu.Trigger лежит поверх
          строки пункта; рендерим только в collapsed, иначе работает
          обычный Collapsible-аккордеон. */}
      {isCollapsed && (
        <Menu.Root>
          <Menu.Trigger
            aria-label={label}
            className="absolute inset-0 z-10 h-8 w-full cursor-pointer outline-none"
          />
          <Menu.Portal>
            <Menu.Positioner side="right" align="start" sideOffset={8} className="z-[60]">
              {/* Шрифт наследуется: базовое font-family висит на body, поэтому
                  портальный popup получает шрифт темы. Раньше здесь стоял
                  font-manrope — правило жило на #root, и портал оставался без
                  шрифта; у wapclick это прибивало popup к Manrope вместо Open Sans. */}
              <Menu.Popup className="min-w-[12rem] rounded-md bg-surface-card p-2 text-sm shadow-modal outline-none">
                <div className="flex flex-col gap-1">
                  {sublinks.map((subItem, subItemIndex) => (
                    <Menu.Item
                      key={subItemIndex}
                      closeOnClick
                      render={(props) => (
                        <Link
                          to={subItem.link}
                          {...props}
                          className={cn(
                            'block w-full rounded-md px-3 py-1.5 text-start text-sm font-medium leading-5',
                            subItem.link === location.pathname
                              ? 'bg-surface-hover text-content-accent'
                              : 'text-content-primary hover:bg-surface-hover hover:text-content-accent',
                          )}
                        >
                          {subItem.label}
                          {subItem.isNew && <NewTag context="popup" />}
                        </Link>
                      )}
                    />
                  ))}
                </div>
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
      )}
    </li>
  );
};

export const MenuItem = ({
  item,
  index,
  disableCollapse = false,
}: {
  item: SidebarMenuItem;
  index: number;
  disableCollapse?: boolean;
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isCollapsed = useAppSelector(selectIsSidebarCollapsed) && !disableCollapse;
  const { link, iconName, label, badge, activePaths, isNew } = item;
  const isLinkActive =
    link === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(link) ||
        Boolean(activePaths?.some((path) => location.pathname.startsWith(path)));

  if (item.sublinks?.length) {
    return <MenuItemWithSublinks item={item} index={index} disableCollapse={disableCollapse} />;
  }

  const onSubLinkClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    dispatch(toggleMobileSidebar());
  };

  const isPills = readIsPills();
  const rootLinkClassName = getRootLinkClassName(isLinkActive, false, isPills);

  return (
    <li className="flex w-full flex-col justify-between" key={index}>
      <div className="flex w-full">
        <Link
          onClick={onSubLinkClick}
          to={link}
          className={rootLinkClassName}
          title={isCollapsed ? label : undefined}
        >
          <ItemIcon name={iconName} isPills={isPills} />
          <div
            className={cn(
              'flex w-full justify-between whitespace-nowrap transition-opacity duration-200',
              isCollapsed ? 'opacity-0' : 'opacity-100',
            )}
          >
            <span className="flex items-center">
              {label}
              {isNew && <NewTag context="row" />}
            </span>
            {Boolean(badge) && badge !== null && badge !== undefined && (
              <Badge count={badge} onAccent={isPills && isLinkActive} />
            )}
          </div>
        </Link>
      </div>
    </li>
  );
};
